import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Loader2, Smartphone, Car, ParkingSquare, ReceiptText, AlertTriangle, Check, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

const TABS = [
  { k: "recharge", l: "Recharge", icon: Smartphone },
  { k: "parking",  l: "Parking",  icon: ParkingSquare },
  { k: "toll",     l: "Toll",     icon: Car },
];

export default function MicroPay() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("recharge");
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const [s, h] = await Promise.all([api.get("/micropay/status"), api.get("/micropay/history?limit=20")]);
      setStatus(s.data);
      setHistory(h.data);
    } catch {}
  };
  useEffect(() => { if (user) load(); }, [user]);

  // Gate clears server-side once a verified wallet top-up lands — no client-side credit.
  const clearGate = () => {
    toast.info(`Top up at least ₹${(status.gate_needs_paise / 100).toFixed(0)} from your wallet to clear the gate`);
    window.location.assign("/wallet");
  };

  if (authLoading) return <div className="p-12 text-center"><Loader2 className="inline animate-spin" /></div>;
  if (!user) return <div className="p-12 text-center text-stone-500">Sign in to use micro-payments.</div>;
  if (!status) return <div className="p-12 text-center"><Loader2 className="inline animate-spin" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-4" data-testid="micropay-page">
      <h1 className="heading text-2xl font-black text-stone-900">Micro-payments</h1>
      <p className="text-xs text-stone-500 mb-3">Recharge · Parking · Toll — one-tap wallet debit.</p>

      {/* Wallet strip */}
      <div className={`rounded-3xl p-4 md:p-5 text-white ${status.balance_paise < 0 ? "bg-gradient-to-br from-red-700 to-red-900" : "bg-gradient-to-br from-emerald-600 to-emerald-900"}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-white/70">Wallet balance</div>
            <div className="text-3xl font-black">{rupees(status.balance_paise)}</div>
          </div>
          <div className="text-right text-xs">
            <div className="opacity-70">Negative visits</div>
            <div className="text-lg font-black">{status.negative_visits} / {status.max_negative_visits}</div>
          </div>
        </div>
        {status.gated && (
          <div className="mt-3 bg-black/25 rounded-xl p-3 text-xs">
            <div className="inline-flex items-center gap-1 font-bold"><AlertTriangle size={12} /> Prepaid gate active</div>
            <div className="mt-1 opacity-90">Top up at least <b>{rupees(status.gate_needs_paise)}</b> to continue.</div>
            <Button onClick={clearGate} className="mt-2 rounded-full bg-white text-red-900 hover:bg-white/90 text-xs font-black" data-testid="micropay-cleargate">
              {`Top up ${rupees(status.gate_needs_paise)} & clear`}
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-4 flex gap-2">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.k;
          return (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold ${active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}
              data-testid={`micropay-tab-${t.k}`}>
              <Icon size={12} /> {t.l}
            </button>
          );
        })}
      </div>

      <div className="mt-3">
        {tab === "recharge" && <RechargeForm onDone={load} disabled={status.gated} />}
        {tab === "parking"  && <ParkingForm  onDone={load} disabled={status.gated} />}
        {tab === "toll"     && <TollForm     onDone={load} disabled={status.gated} />}
      </div>

      {/* History */}
      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2 flex items-center gap-1">
          <ReceiptText size={11} /> Recent transactions
        </div>
        {history.length === 0 ? (
          <div className="text-center py-8 text-stone-500 text-xs">No transactions yet.</div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
            {history.map(h => (
              <div key={h.txn_id} className="p-3 flex items-center justify-between text-sm" data-testid={`micropay-txn-${h.txn_id}`}>
                <div>
                  <div className="font-bold uppercase text-[10px] text-stone-700">{h.service}</div>
                  <div className="text-xs text-stone-500 line-clamp-1">
                    {h.service === "recharge" && `${h.meta?.operator} · ${h.meta?.mobile}`}
                    {h.service === "parking" && `${h.meta?.lot_name} · ${h.meta?.hours}h`}
                    {h.service === "toll" && `${h.meta?.plaza_name} · ${h.meta?.vehicle_class}`}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-black text-[#E05E36]">-{rupees(h.amount_paise)}</div>
                  <div className="text-[10px] text-stone-500">bal {rupees(h.balance_after_paise)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// One Idempotency-Key per payment attempt: a network retry reuses it (no double charge); a definitive answer resets it.
function usePayKey() {
  const ref = useRef(null);
  return async (path, body) => {
    if (!ref.current) ref.current = `mp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    try {
      const r = await api.post(path, body, { headers: { "Idempotency-Key": ref.current } });
      ref.current = null;
      return r;
    } catch (e) {
      if (e?.response) ref.current = null;
      throw e;
    }
  };
}

// --------------- Forms ---------------
function RechargeForm({ onDone, disabled }) {
  const pay = usePayKey();
  const [form, setForm] = useState({ mobile: "", operator: "airtel", amount: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!/^\d{10}$/.test(form.mobile)) { toast.error("10-digit mobile"); return; }
    if (!(+form.amount > 0)) { toast.error("Enter amount"); return; }
    setBusy(true);
    try {
      await pay("/micropay/recharge", {
        mobile: form.mobile, operator: form.operator,
        amount_paise: Math.round(+form.amount * 100),
      });
      toast.success(`Recharge of ₹${form.amount} placed`);
      setForm({ mobile: "", operator: "airtel", amount: "" });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-2">
      <Input placeholder="Mobile number" value={form.mobile} maxLength={10} onChange={e => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })} data-testid="mp-recharge-mobile" />
      <select value={form.operator} onChange={e => setForm({ ...form, operator: e.target.value })} className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm" data-testid="mp-recharge-operator">
        <option value="airtel">Airtel</option>
        <option value="jio">Jio</option>
        <option value="vi">Vi</option>
        <option value="bsnl">BSNL</option>
      </select>
      <Input placeholder="Amount (₹)" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="mp-recharge-amount" />
      <Button onClick={submit} disabled={busy || disabled || !form.mobile || !form.amount} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a] disabled:opacity-50" data-testid="mp-recharge-submit">
        {busy ? <Loader2 className="animate-spin" size={14} /> : `Recharge ₹${form.amount || "0"}`}
      </Button>
    </div>
  );
}

function ParkingForm({ onDone, disabled }) {
  const pay = usePayKey();
  const [form, setForm] = useState({ lot_name: "", hours: "1", rate: "20" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.lot_name.trim()) { toast.error("Lot name required"); return; }
    setBusy(true);
    try {
      await pay("/micropay/parking", {
        lot_name: form.lot_name, hours: +form.hours,
        rate_paise_per_hour: Math.round(+form.rate * 100),
      });
      toast.success("Parking session paid");
      setForm({ lot_name: "", hours: "1", rate: "20" });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-2">
      <Input placeholder="Parking lot / mall name" value={form.lot_name} onChange={e => setForm({ ...form, lot_name: e.target.value })} data-testid="mp-parking-lot" />
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Hours" type="number" step="0.5" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} data-testid="mp-parking-hours" />
        <Input placeholder="Rate ₹/hr" type="number" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} data-testid="mp-parking-rate" />
      </div>
      <div className="text-xs text-stone-500">Total ≈ <b>₹{(+form.hours * +form.rate).toFixed(2)}</b></div>
      <Button onClick={submit} disabled={busy || disabled || !form.lot_name} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a] disabled:opacity-50" data-testid="mp-parking-submit">
        {busy ? <Loader2 className="animate-spin" size={14} /> : "Pay & park"}
      </Button>
    </div>
  );
}

function TollForm({ onDone, disabled }) {
  const pay = usePayKey();
  const [form, setForm] = useState({ plaza_name: "", vehicle_class: "car", amount: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.plaza_name.trim() || !(+form.amount > 0)) { toast.error("Fill all fields"); return; }
    setBusy(true);
    try {
      await pay("/micropay/toll", {
        plaza_name: form.plaza_name, vehicle_class: form.vehicle_class,
        amount_paise: Math.round(+form.amount * 100),
      });
      toast.success("Toll paid");
      setForm({ plaza_name: "", vehicle_class: "car", amount: "" });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-2">
      <Input placeholder="Toll plaza name" value={form.plaza_name} onChange={e => setForm({ ...form, plaza_name: e.target.value })} data-testid="mp-toll-plaza" />
      <div className="grid grid-cols-2 gap-2">
        <select value={form.vehicle_class} onChange={e => setForm({ ...form, vehicle_class: e.target.value })} className="border border-stone-300 rounded-lg px-3 py-2 text-sm" data-testid="mp-toll-class">
          <option value="bike">Bike</option>
          <option value="car">Car</option>
          <option value="suv">SUV</option>
          <option value="truck">Truck</option>
          <option value="bus">Bus</option>
        </select>
        <Input placeholder="Amount (₹)" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="mp-toll-amount" />
      </div>
      <Button onClick={submit} disabled={busy || disabled || !form.plaza_name || !form.amount} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a] disabled:opacity-50" data-testid="mp-toll-submit">
        {busy ? <Loader2 className="animate-spin" size={14} /> : `Pay ₹${form.amount || "0"}`}
      </Button>
    </div>
  );
}
